import React from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { cn } from '../../utils/cn'
import { useAuth } from '../../contexts/AuthContext'
import { SocketCloudIcon } from '../ui/SocketCloudIcon'
import {
  LayoutDashboard,
  Network,
  GitBranch,
  CalendarClock,
  Shield,
  Settings,
  Activity,
  Database,
  AlertTriangle,
  HardDrive,
  ChevronLeft,
  ChevronRight,
  Bot,
  Crown,
  UserCog,
  ShieldCheck,
} from 'lucide-react';

interface SidebarProps {
  collapsed: boolean
  onToggleCollapse: () => void
  mobileOpen?: boolean
  onMobileClose?: () => void
}

const navigationItems = [
  // nav-id: socketDashboardNav
  {
    name: 'Dashboard',
    path: '/',
    icon: LayoutDashboard,
    badge: null,
  },
  // nav-id: socketConsensusNav
  {
    name: 'Consensus',
    path: '/consensus',
    icon: GitBranch,
    badge: null,
  },
  // nav-id: socketAiMcpNav
  {
    name: 'AI/MCP',
    path: '/ai-mcp',
    icon: Bot,
    badge: null,
  },
  // nav-id: socketNetworkNav
  {
    name: 'Network',
    path: '/network',
    icon: Activity,
    badge: null,
  },
  // nav-id: socketCrdtNav
  {
    name: 'CRDT Operations',
    path: '/crdt',
    icon: Database,
    badge: null,
  },
  // nav-id: socketSecurityNav
  {
    name: 'Security',
    path: '/security',
    icon: Shield,
    badge: null,
  },
  // nav-id: socketStorageNav
  {
    name: 'Storage',
    path: '/storage',
    icon: HardDrive,
    badge: null,
  },
  // nav-id: socketOrchestrationNav
  {
    name: 'Orchestration',
    path: '/orchestration',
    icon: CalendarClock,
    badge: null,
  },
  // nav-id: socketAlertsNav
  {
    name: 'Alerts',
    path: '/alerts',
    icon: AlertTriangle,
    badge: null,
  },
  // nav-id: socketConfigurationNav
  {
    name: 'Configuration',
    path: '/config',
    icon: Settings,
    badge: null,
  },
];

export const Sidebar: React.FC<SidebarProps> = ({ 
  collapsed, 
  onToggleCollapse,
  mobileOpen = false,
  onMobileClose
}) => {
  const location = useLocation();
  const { checkPermission, hasPermission } = useAuth();
  
  // Check if user has platform admin permissions
  const isPlatformAdmin = checkPermission('system', 'admin') || 
                         checkPermission('system', 'tenants:read');

  // Check if user has tenant management permissions
  const canManageTenant = hasPermission('tenant.users.view') || 
                         hasPermission('tenant.users.manage') ||
                         hasPermission('tenant.roles.manage') ||
                         hasPermission('tenant.update');

  const adminItems = [
    // nav-id: socketAdminPlatformNav
    {
      name: 'Platform Overview',
      path: '/admin/platform',
      icon: Crown,
      badge: null,
    },
  ];

  const tenantItems = [
    // nav-id: socketTenantUsersNav
    {
      name: 'Users',
      path: '/tenant/users',
      icon: UserCog,
      badge: null,
    },
    // nav-id: socketTenantRolesNav
    {
      name: 'Roles & Permissions',
      path: '/tenant/roles',
      icon: ShieldCheck,
      badge: null,
    },
  ];

  return (
    <aside
      className={cn(
        'fixed left-0 top-0 z-40 h-full sidebar-glass transition-all duration-300',
        collapsed ? 'w-16' : 'w-64',
        mobileOpen && 'mobile-open',
      )}
    >
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-white/10">
        {!collapsed && (
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 bg-gradient-to-br from-accent-blue to-accent-blue-light rounded-lg flex items-center justify-center shadow-lg animate-glow">
              <SocketCloudIcon size={20} color="white" />
            </div>
            <div>
              <h1 className="text-lg suse-bold text-console-text">SocketCloud</h1>
              <p className="text-xs text-console-muted suse-light">Enterprise Console</p>
            </div>
          </div>
        )}
        
        <button
          onClick={onToggleCollapse}
          className="p-1.5 rounded-lg text-console-muted hover:text-console-text hover:bg-secondary-800/50 transition-colors"
          title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
        >
          {collapsed ? (
            <ChevronRight className="w-4 h-4" />
          ) : (
            <ChevronLeft className="w-4 h-4" />
          )}
        </button>
      </div>

      {/* System Status */}
      {!collapsed && (
        <div className="p-4 border-b border-white/10">
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-xs text-console-muted uppercase tracking-wide suse-medium">System Status</span>
              <div className="status-indicator status-online !mr-0" />
            </div>
            <div className="space-y-1 text-xs">
              <div className="flex justify-between">
                <span className="text-console-muted">Nodes Online</span>
                <span className="text-console-text suse-medium">12/15</span>
              </div>
              <div className="flex justify-between">
                <span className="text-console-muted">Consensus</span>
                <span className="text-status-online suse-medium">Healthy</span>
              </div>
              <div className="flex justify-between">
                <span className="text-console-muted">Network</span>
                <span className="text-status-online suse-medium">Active</span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Navigation */}
      <nav className="flex-1 p-4">
        <ul className="space-y-2">
          {navigationItems.map((item) => {
            const isActive = location.pathname === item.path || 
              (item.path !== '/' && location.pathname.startsWith(item.path));
            
            return (
              <li key={item.path}>
                <NavLink
                  to={item.path}
                  onClick={onMobileClose}
                  className={cn(
                    'nav-item w-full',
                    collapsed && 'justify-center',
                    isActive && 'active',
                  )}
                  title={collapsed ? item.name : undefined}
                >
                  <item.icon className={cn('w-5 h-5 flex-shrink-0', !collapsed && 'mr-3')} />
                  {!collapsed && (
                    <>
                      <span className="truncate">{item.name}</span>
                      {item.badge && (
                        <span className="ml-auto bg-danger-600 text-white text-xs rounded-full px-2 py-0.5 min-w-[1.25rem] text-center">
                          {item.badge}
                        </span>
                      )}
                    </>
                  )}
                </NavLink>
              </li>
            );
          })}
          
          {/* Platform Admin Section */}
          {isPlatformAdmin && (
            <>
              {!collapsed && (
                <li className="pt-4">
                  <div className="text-xs text-console-muted uppercase tracking-wide suse-medium px-3 py-2">
                    Platform Administration
                  </div>
                </li>
              )}
              {adminItems.map((item) => {
                const isActive = location.pathname === item.path || 
                  (item.path !== '/' && location.pathname.startsWith(item.path));
                
                return (
                  <li key={item.path}>
                    <NavLink
                      to={item.path}
                      onClick={onMobileClose}
                      className={cn(
                        'nav-item w-full',
                        collapsed && 'justify-center',
                        isActive && 'active',
                      )}
                      title={collapsed ? item.name : undefined}
                    >
                      <item.icon className={cn('w-5 h-5 flex-shrink-0', !collapsed && 'mr-3')} />
                      {!collapsed && (
                        <>
                          <span className="truncate">{item.name}</span>
                          {item.badge && (
                            <span className="ml-auto bg-danger-600 text-white text-xs rounded-full px-2 py-0.5 min-w-[1.25rem] text-center">
                              {item.badge}
                            </span>
                          )}
                        </>
                      )}
                    </NavLink>
                  </li>
                );
              })}
            </>
          )}
          
          {/* Tenant Management Section */}
          {canManageTenant && (
            <>
              {!collapsed && (
                <li className="pt-4">
                  <div className="text-xs text-console-muted uppercase tracking-wide suse-medium px-3 py-2">
                    Tenant Management
                  </div>
                </li>
              )}
              {tenantItems.map((item) => {
                const isActive = location.pathname === item.path || 
                  (item.path !== '/' && location.pathname.startsWith(item.path));
                
                return (
                  <li key={item.path}>
                    <NavLink
                      to={item.path}
                      onClick={onMobileClose}
                      className={cn(
                        'nav-item w-full',
                        collapsed && 'justify-center',
                        isActive && 'active',
                      )}
                      title={collapsed ? item.name : undefined}
                    >
                      <item.icon className={cn('w-5 h-5 flex-shrink-0', !collapsed && 'mr-3')} />
                      {!collapsed && (
                        <>
                          <span className="truncate">{item.name}</span>
                          {item.badge && (
                            <span className="ml-auto bg-danger-600 text-white text-xs rounded-full px-2 py-0.5 min-w-[1.25rem] text-center">
                              {item.badge}
                            </span>
                          )}
                        </>
                      )}
                    </NavLink>
                  </li>
                );
              })}
            </>
          )}
        </ul>
      </nav>

      {/* Footer -- canonical BUILD_VERSION display per
          .claude/rules/build-version-convention.md (YYYY.MMDD.HHMM UTC) +
          memory feedback_build_version_location_left_nav_bottom.md
          (bottom-of-left-nav, ZERO TOLERANCE for hardcoded literal). */}
      {!collapsed && (
        <div className="p-4 border-t border-white/10">
          <span
            className="text-xs font-mono suse-medium text-console-muted"
            title="Build version"
          >
            v{import.meta.env.VITE_BUILD_VERSION || '0.0.0-dev'}
          </span>
        </div>
      )}
    </aside>
  );
};